import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { setSelectedCard } from "../../state/actions/Cards";
import { resolvePage } from "./pageDict";
import { Portal } from "../../components/atoms";
import SubHeader from "./SubHeader";
import {
  getHasNext,
  getIsIntermediate,
  getQueryType,
} from "../../state/selectors/general.selectors";
import CardsContainerSide from "../../components/cards/cardsContainer/CardsContainerSide";
import CardsContainerTop from "../../components/cards/cardsContainer/CardsContainerTop";
import AdminMessage from "../../components/AdminMessage";
import CardList from "./CardList";
import {
  getSelectedCard,
  getVariantQueryMode,
  getCardsIsOnSide,
  getCardsIsViewAll,
} from "../../state/selectors/CardsUI.selectors";
import { setProteinViewerDataToDefault } from "../../state/actions/genericActions";
import ProteinResultsContainer from "../../components/3d-protein-viewer/ProteinResultsContainer";

function VarsomePage(props) {
  const {
    queryType,
    selectedCard,
    selectCard,
    variantQueryMode,
    isCardsOnSide,
    isIntermediate,
    hasNext,
    isLoading,
    isCompact,
    variantResult,
    setProteinViewerDataToDefaultAction,
    isViewAll,
  } = props;

  const isTP53 = (variantResult?.genes || [])
    .map((x) => x.symbol)
    .find((x) => x.match(/TP53/gi));

  const isOnSide = isCardsOnSide;

  const varsomePage = useMemo(() => {
    return resolvePage(queryType, variantQueryMode, Boolean(isTP53));
  }, [queryType, variantQueryMode, isTP53]);

  const PageComp = varsomePage?.page || null;
  const cards = varsomePage?.cards || [];
  const hasIntermediateScreen = isIntermediate && hasNext;
  const hasCards = cards && cards.length;
  const showCards = !hasIntermediateScreen && !isLoading;
  const showCardsOnTop = hasCards && showCards && !isOnSide;
  const showCardsOnSide = hasCards && showCards && isOnSide;

  const attachToId = "varsome-menu-container";

  useEffect(() => {
    return () => {
      setProteinViewerDataToDefaultAction({
        results: null,
        error: false,
        finalTranscripts: [],
      });
    };
  }, []);

  useEffect(() => {
    const { hash } = window.location;
    const isCommunityHash = hash === "#community-contributions";
    const firstCard = isCommunityHash
      ? cards.find((x) => x.id === "communityContributions")
      : cards[0] || {};
    const selectedFound = cards.find((x) => x.id === selectedCard);
    const card = selectedFound || firstCard;
    if (card) {
      selectCard(card.id);
      return () => {};
    }
    return () => {};
  }, [cards, selectedCard, selectCard]);

  useEffect(() => {
    const { hash } = window.location;
    const isCommunityHash = hash === "#community-contributions";
    // delay so that it scrolls correctly - after compns. render - this logic should ideally get moved to the comp. wrapper.
    const timeout = setTimeout(() => {
      const communityCard = document.getElementById("communityContributions");
      if (isCommunityHash && communityCard && isViewAll) {
        communityCard.click();
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [cards]);

  useEffect(() => {
    const appEl = document.getElementById("varsome-app");
    const headerEl = document.getElementById("varsome-menu-container");

    if (isCompact) {
      appEl.classList.add("compact");
      headerEl.classList.add("compact");
    } else {
      appEl.classList.remove("compact");
      headerEl.classList.remove("compact");
    }
  }, [isCompact]);

  return (
    <>
      <Portal toId={attachToId}>
        <>
          <div className="tw-shadow-xl">
            <SubHeader />
          </div>
          {showCardsOnTop ? (
            <div className="tw-relative">
              <CardsContainerTop cards={cards} />
            </div>
          ) : null}
          <div className={showCardsOnTop ? "tw-h-4" : "tw-h-10"} />
        </>
      </Portal>

      <div className="tw-relative tw-mx-auto tw-px-10 tw-pb-10 tw-max-w-1800 tw-w-full">
        <AdminMessage position="top" />

        <div className="tw-flex tw-w-full">
          <div
            id="components-start"
            className="tw-overflow-auto tw-flex-grow tw--my-6 tw-py-6"
          >
            <PageComp cards={cards || []} />
          </div>

          {showCardsOnSide ? (
            <>
              <div className="tw-w-6 tw-flex-shrink-0" />
              <CardsContainerSide>
                <div className="tw-w-72">
                  <CardList cards={cards} />
                </div>
              </CardsContainerSide>
            </>
          ) : null}
        </div>
      </div>
      <ProteinResultsContainer />
    </>
  );
}

VarsomePage.propTypes = {
  queryType: PropTypes.string,
  selectCard: PropTypes.func.isRequired,
  setProteinViewerDataToDefaultAction: PropTypes.func.isRequired,
  variantQueryMode: PropTypes.string,
  selectedCard: PropTypes.string,
  isCardsOnSide: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool,
  isIntermediate: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isLoading: PropTypes.bool,
  isCompact: PropTypes.bool,
  variantResult: PropTypes.shape({
    genes: PropTypes.arrayOf(PropTypes.shape({ symbol: PropTypes.string })),
    variant_id: PropTypes.string,
    gene_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isViewAll: PropTypes.bool,
};

VarsomePage.defaultProps = {
  queryType: "",
  variantQueryMode: "",
  selectedCard: null,
  isIntermediate: "",
  hasNext: false,
  isLoading: false,
  isCompact: false,
  variantResult: { genes: [], variant_id: "", gene_id: "" },
  isViewAll: false,
};

const mapStateToProps = (state) => ({
  selectedCard: getSelectedCard(state),
  isCardsOnSide: getCardsIsOnSide(state),
  variantQueryMode: getVariantQueryMode(state),
  queryType: getQueryType(state),
  isIntermediate: getIsIntermediate(state),
  hasNext: getHasNext(state),
  isCompact: state.cardOptions.isCompact,
  variantResult: state.variantResult,
  isViewAll: getCardsIsViewAll(state),
});

const mapDispatchToProps = (dispatch) => ({
  selectCard: (params) => dispatch(setSelectedCard(params)),
  setProteinViewerDataToDefaultAction: (params) =>
    dispatch(setProteinViewerDataToDefault(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VarsomePage);
